import { Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Theme } from "@radix-ui/themes";
import type { ComponentType } from "react";
import type {
  PrerenderArguments,
  PrerenderResult,
} from "vite-prerender-plugin";
import { SITE_URL, siteUrl } from "./lib/site";

// Each page component below is dynamically imported (rather than statically,
// as it was before) so it lands in its own build chunk - matching the
// client's own React.lazy() split in App.tsx - instead of being merged into
// this prerender script's shared chunk. A static import here would be
// reachable from both the prerender entry (this file) and the client entry,
// so the bundler would fuse them into one chunk that the client eagerly
// `modulepreload`s on every visit, silently re-inflating every visitor's
// initial download with the full Terms/Privacy/LearnMore/SafetyProtocol page
// code regardless of which page they're on.
async function loadPageComponent(
  routePath: string,
): Promise<ComponentType | null> {
  switch (routePath) {
    case "/":
      return (await import("./routes/pages/HomePage")).HomePage;
    case "/learn-more":
      return (await import("./routes/pages/LearnMorePage")).LearnMorePage;
    case "/terms-conditions":
      return (await import("./routes/pages/TermsPage")).TermsPage;
    case "/privacy":
      return (await import("./routes/pages/PrivacyPage")).PrivacyPage;
    case "/safety-protocol":
      return (await import("./routes/pages/SafetyProtocolPage"))
        .SafetyProtocolPage;
    case "/welcome":
      return (await import("./routes/pages/WelcomePage")).WelcomePage;
    case "/checkout":
      return () => null;
    default:
      return null;
  }
}

type RouteSeo = {
  title: string;
  description?: string;
  suppressSocialCard?: boolean;
  suppressSocialPreview?: boolean;
};

const routeSeo: Record<string, RouteSeo> = {
  "/": {
    title: "Iris",
    description:
      "Iris is your non-clinical thought partner, available 24/7 on iMessage. Experience support that feels like a breeze.",
  },
  "/learn-more": {
    title: "Learn More | Iris",
    description:
      "The story of Iris — a new kind of AI thought partner designed to help you understand and befriend your mind.",
  },
  "/privacy": {
    title: "Privacy Policy | Iris",
    description:
      "Learn how IrisChat handles your personal information and protects your privacy.",
  },
  "/terms-conditions": {
    title: "Terms & Conditions | Iris",
    description: "Terms of use for the IrisChat AI thought partner service.",
    suppressSocialPreview: true,
  },
  "/safety-protocol": {
    title: "Safety Protocol | Iris",
    description:
      "IrisChat's commitment to user safety, crisis response, and mental health support protocols.",
  },
  "/welcome": {
    title: "Access Unlocked! | Iris",
    description:
      "Start your journey with Iris - your AI thought partner on iMessage.",
  },
  "/checkout": {
    title: "Checkout | Iris",
    suppressSocialCard: true,
  },
};

export async function prerender({
  url,
}: PrerenderArguments): Promise<PrerenderResult> {
  // Dynamically imported so this server-only SSR code (react-dom/server,
  // react-router-dom/server) never ends up in the client bundle - a static
  // import here would ship it to every browser even though it's build-time
  // only. See vite-prerender-plugin's README ("Anything you want to be
  // server-only ... should be dynamically imported in the prerender
  // function").
  const { renderToString } = await import("react-dom/server");
  const { StaticRouter } = await import("react-router-dom/server");

  const routePath = url === "/" ? "/" : url.replace(/\/+$/, "");
  const PageComponent = await loadPageComponent(routePath);

  const html = renderToString(
    <HelmetProvider>
      <StaticRouter location={url}>
        <Theme
          appearance="light"
          accentColor="gray"
          grayColor="gray"
          radius="large"
        >
          {PageComponent ? <PageComponent /> : <Navigate to="/" replace />}
        </Theme>
      </StaticRouter>
    </HelmetProvider>,
  );

  const seo = routeSeo[routePath] ?? routeSeo["/"];
  // Emit trailing-slash canonical/og:url for non-root routes so the prerendered
  // HTML head matches the site's canonical (trailing-slash) URLs and crawlers
  // don't follow a redirect. The output file path is unaffected: the prerender
  // plugin strips leading/trailing slashes when deriving the asset name.
  const canonicalPath = url === "/" ? "/" : `${url.replace(/\/+$/, "")}/`;
  const canonical = siteUrl(canonicalPath);

  type HeadEl = { type: string; props: Record<string, string> };
  const elementList: HeadEl[] = [
    { type: "link", props: { rel: "canonical", href: canonical } },
  ];

  if (seo.suppressSocialCard) {
    elementList.push({
      type: "meta",
      props: { name: "robots", content: "noindex, nofollow, noarchive" },
    });
  }

  if (!seo.suppressSocialCard && !seo.suppressSocialPreview) {
    const socialImage = `${SITE_URL}/social-card.webp`;
    elementList.push(
      { type: "meta", props: { property: "og:type", content: "website" } },
      { type: "meta", props: { property: "og:url", content: canonical } },
      { type: "meta", props: { property: "og:title", content: seo.title } },
      { type: "meta", props: { property: "og:image", content: socialImage } },
      {
        type: "meta",
        props: { property: "og:image:width", content: "1280" },
      },
      {
        type: "meta",
        props: { property: "og:image:height", content: "832" },
      },
      {
        type: "meta",
        props: { name: "twitter:card", content: "summary_large_image" },
      },
      { type: "meta", props: { name: "twitter:title", content: seo.title } },
      { type: "meta", props: { name: "twitter:image", content: socialImage } },
    );
  }

  if (seo.description) {
    elementList.push({
      type: "meta",
      props: { name: "description", content: seo.description },
    });

    if (!seo.suppressSocialCard && !seo.suppressSocialPreview) {
      elementList.push(
        {
          type: "meta",
          props: { property: "og:description", content: seo.description },
        },
        {
          type: "meta",
          props: { name: "twitter:description", content: seo.description },
        },
      );
    }
  }

  return {
    html,
    links: new Set([
      "/learn-more",
      "/privacy",
      "/terms-conditions",
      "/safety-protocol",
      "/welcome",
      "/checkout",
    ]),
    head: {
      title: seo.title,
      elements: new Set(elementList),
    },
  };
}
